// Miky Motorsport - entry point (async boot)
import { initRapier, createWorld } from './physics/world.js';
import { SceneManager } from './render/scene.js';
import { ChaseCamera } from './camera.js';
import { InputManager } from './input.js';
import { ParticleSystem } from './particles.js';
import { buildTrack } from './track.js';
import { Game } from './game.js';
import { loadProfile } from './storage.js';
import { AudioEngine } from './audio.js';

const canvas = document.getElementById('game-canvas');
const loadingEl = document.getElementById('loading');
const loadingFill = document.getElementById('loading-fill');
const loadingMsg = document.getElementById('loading-msg');

function setLoading(p, msg) {
  if (loadingFill) loadingFill.style.width = `${Math.round(p * 100)}%`;
  if (msg && loadingMsg) loadingMsg.textContent = msg;
}

async function boot() {
  try {
    setLoading(0.1, 'Inicializuji fyziku (Rapier WASM)...');
    await initRapier();

    setLoading(0.4, 'Stavim svet...');
    const world = createWorld();
    const sceneMgr = new SceneManager(canvas);
    const camera = new ChaseCamera(window.innerWidth / window.innerHeight);
    const input = new InputManager();
    const particles = new ParticleSystem(sceneMgr.scene);

    setLoading(0.5, 'Nacitam prostredi (HDRI)...');
    await sceneMgr.loadHDREnvironment('./env/sky.hdr', { background: false });

    setLoading(0.7, 'Pripravuji trat...');
    const track = buildTrack(world, sceneMgr.scene);

    setLoading(0.9, 'Nacitam profil...');
    const profile = loadProfile();
    const audio = new AudioEngine();
    // AudioContext resume po prvnim user gestu - po prvni inicializaci OBA listenery
    // odstran (jinak by se volaly pri kazdem vstupu po celou dobu zivota stranky).
    const initAudio = () => {
      audio.init(profile.settings?.masterVolume); // aplikuj ulozenou hlasitost (F-303)
      window.removeEventListener('pointerdown', initAudio);
      window.removeEventListener('keydown', initAudio);
    };
    window.addEventListener('pointerdown', initAudio);
    window.addEventListener('keydown', initAudio);

    const game = new Game({ sceneMgr, world, track, camera, input, particles, profile, audio });

    // Post-processing pipeline (bloom + ACES + vignette + SMAA)
    sceneMgr.setupPost(camera.cam);

    // --- Expose pro Chrome DevTools MCP / debug ---
    window.__game = game;
    window.__debug = () => game.debugState();

    // --- Resize ---
    function onResize() {
      const w = window.innerWidth;
      const h = window.innerHeight;
      sceneMgr.resize(w, h);
      camera.resize(w / h);
    }
    window.addEventListener('resize', onResize);
    onResize();

    // --- Pri skrytem tabu zastav loop (setri CPU/GPU, zastavi hudbu); zavod zustane pauznuty ---
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) { game.pause(); game.stop(); }
      else game.start();
    });

    // --- WebGL context loss handling ---
    canvas.addEventListener(
      'webglcontextlost',
      (e) => {
        e.preventDefault();
        game.stop();
        if (loadingEl) {
          loadingMsg.textContent = 'GPU kontext ztracen, cekam na obnoveni...';
          loadingEl.classList.remove('hidden');
        }
      },
      false
    );
    canvas.addEventListener('webglcontextrestored', () => {
      if (loadingEl) loadingEl.classList.add('hidden');
      game.start();
    });

    setLoading(1.0, 'Hotovo!');
    setTimeout(() => loadingEl && loadingEl.classList.add('hidden'), 250);
    game.start();
  } catch (err) {
    console.error('Boot failed:', err);
    if (loadingMsg) loadingMsg.textContent = 'Chyba pri startu: ' + (err && err.message ? err.message : String(err));
  }
}

boot();
